import React, { useState } from 'react'
import { submitFeedback } from '../services/feedbackApi'
import { useToast } from './ToastProvider'

const GOOGLE_MAPS_REVIEW_URL = 'https://maps.app.goo.gl/sEacFqk1YxFJq3A3A'

export default function GoogleMapsFeedback({ visitorName = 'Guest', visitorEmail = '' }) {
  const toast = useToast()
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [rating, setRating] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmitFeedback = async () => {
    if (!rating && !feedback.trim()) {
      toast.error('Please provide a rating or feedback')
      return
    }

    try {
      setLoading(true)
      await submitFeedback(visitorName, visitorEmail, rating, feedback)
      setSubmitted(true)
      toast.success('Thank you for your feedback!')
      setTimeout(() => {
        setShowFeedback(false)
        setFeedback('')
        setRating(0)
        setSubmitted(false)
      }, 2000)
    } catch (err) {
      toast.error(err.message || 'Failed to submit feedback')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-6">
        <h3 className="font-semibold text-blue-900 mb-2">Share Your Experience</h3>
        <p className="text-sm text-blue-700 mb-4">
          We'd love to hear about your visit to igus. Your feedback helps us improve our services.
        </p>

        <div className="space-y-3">
          {/* Rating */}
          <div>
            <label className="text-xs font-medium text-blue-900 block mb-2">How was your experience?</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <svg
                    className={`h-6 w-6 ${
                      star <= rating
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300 fill-current hover:text-yellow-300'
                    }`}
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Feedback Textarea */}
          {showFeedback && (
            <div>
              <label className="text-xs font-medium text-blue-900 block mb-2">Tell us more (optional)</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share your feedback about your visit..."
                className="w-full h-24 rounded border border-blue-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            {!submitted ? (
              <>
                {!showFeedback ? (
                  <>
                    <button
                      onClick={() => setShowFeedback(true)}
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                    >
                      Leave Feedback
                    </button>
                    <a
                      href={GOOGLE_MAPS_REVIEW_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 rounded-lg border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                      </svg>
                      Google Review
                    </a>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSubmitFeedback}
                      disabled={!feedback.trim() && rating === 0 || loading}
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                    <button
                      onClick={() => {
                        setShowFeedback(false)
                        setFeedback('')
                        setRating(0)
                      }}
                      className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="w-full rounded-lg bg-green-50 border border-green-200 p-3 text-center">
                <p className="text-sm font-medium text-green-700">✓ Thank you for your feedback!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
